import React, { useState, useEffect } from 'react';
import { Timer, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SLACountdownProps {
  deadline: string; // ISO format
  status?: "active" | "completed" | "overdue";
  className?: string;
}

export const SLACountdown: React.FC<SLACountdownProps> = ({ 
  deadline, 
  status: initialStatus = "active",
  className 
}) => {
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [percentage, setPercentage] = useState<number>(100);
  const [status, setStatus] = useState(initialStatus);

  useEffect(() => {
    if (status === "completed") return;

    const timer = setInterval(() => {
      const now = new Date();
      const end = new Date(deadline);
      const diff = end.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft("EXPIRED");
        setPercentage(0);
        setStatus("overdue");
        clearInterval(timer);
        return;
      }

      const totalSLASeconds = 4 * 3600; // 4h standard for demo
      const currentSeconds = diff / 1000;
      setPercentage(Math.min(100, (currentSeconds / totalSLASeconds) * 100));

      const h = Math.floor(currentSeconds / 3600);
      const m = Math.floor((currentSeconds % 3600) / 60);
      const s = Math.floor(currentSeconds % 60);

      setTimeLeft(`${h}h ${m}m ${s}s`);
    }, 1000);

    return () => clearInterval(timer);
  }, [deadline, status]);

  if (status === "completed") {
    return (
      <div className={cn("flex items-center gap-2 text-emerald-500 font-bold text-xs uppercase tracking-widest", className)}>
        <CheckCircle2 className="h-3.5 w-3.5" />
        SLA CUMPLIDO
      </div>
    );
  }

  const isWarning = percentage < 25;
  const isDanger = percentage < 10;

  return (
    <div className={cn("space-y-1.5 min-w-[120px]", className)}>
      <div className="flex items-center justify-between">
        <div className={cn(
          "flex items-center gap-1.5 text-[10px] font-black uppercase tracking-tighter",
          isDanger ? "text-red-600 animate-pulse" : isWarning ? "text-amber-600" : "text-slate-400"
        )}>
          <Timer className="h-3 w-3" />
          {isDanger ? "Crítico" : isWarning ? "Atención" : "Tiempo de Respuesta"}
        </div>
        <span className={cn(
          "text-xs font-mono font-bold",
          isDanger ? "text-red-700" : isWarning ? "text-amber-700" : "text-slate-700"
        )}>
          {timeLeft}
        </span>
      </div>
      
      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-50 shadow-inner">
        <div 
          className={cn(
            "h-full transition-all duration-1000 ease-linear rounded-full",
            isDanger ? "bg-red-600" : isWarning ? "bg-amber-500" : "bg-blue-600"
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};
